import { useEffect, useState } from "react";
import axios from "axios";
import { Users } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/lib/api";
import { useTaal } from "@/contexts/TaalContext";
import TeamRolLabel from "./TeamRolLabel";

/**
 * GT1 en GT2 (GO Frank 2 november 2026): bij welk team een repo hoort, en jouw rol erop.
 *
 * De eigenaar kiest Persoonlijk of een team waar hij lid of admin van is (PUT /repos/{id}/team; de backend toetst het
 * lidmaatschap). Een teamlid dat de repo van een ander opent, ziet alleen zijn rol: viewer leest en werkt met issues,
 * member maakt ook takken en pull requests, admin voegt ook samen. Naar main gaat in een teamrepo pas iets na
 * goedkeuring door een ander teamlid dan de maker (GT3, de kaart Samenvoegen zegt het).
 */
export default function RepoTeam({ repo }) {
  const { t } = useTaal();
  const [teams, setTeams] = useState([]);
  const [team, setTeam] = useState(repo.team_id || "");
  // Glasronde 0.9.234 punt 2: de eigen rol en de naam van het team, ook meteen na het kiezen.
  const [label, setLabel] = useState({ naam: repo.team_naam || null, rol: repo.rol || null });
  const eigen = repo.eigen !== false;

  useEffect(() => {
    if (!eigen) return;
    axios.get(`${API}/teams`).then((r) => setTeams(Array.isArray(r.data) ? r.data : [])).catch(() => setTeams([]));
  }, [eigen]);

  async function kies(e) {
    const nieuw = e.target.value;
    try {
      const r = await axios.put(`${API}/repos/${repo.id}/team`, { team_id: nieuw || null });
      setTeam(r.data?.team_id || "");
      setLabel({ naam: r.data?.team_naam || null, rol: r.data?.rol || null });
      toast.success(nieuw ? t("De repo hoort nu bij het team.") : t("De repo is nu persoonlijk."));
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? t(d) : t("Het team kon niet worden gekozen."));
    }
  }

  if (!eigen) {
    return (
      <p className="flex flex-wrap items-center gap-1 text-xs text-muted-foreground" data-testid="repo-rol">
        {t("Teamrepo van een ander lid.")}
        <TeamRolLabel teamNaam={repo.team_naam || t("onbekend team")} rol={repo.rol} />
      </p>
    );
  }
  if (teams.length === 0 && !team) return null;
  return (
    <label className="flex items-center gap-1 text-xs" data-testid="repo-team">
      <Users className="h-3 w-3" aria-hidden="true" />
      {t("Team")}
      <select value={team} onChange={kies} aria-label={t("Team van deze repo")}
              className="rounded-md border bg-transparent px-1 py-1" data-testid="repo-team-kiezer">
        <option value="">{t("Persoonlijk")}</option>
        {teams.map((tm) => <option key={tm.id} value={tm.id}>{tm.name}</option>)}
      </select>
      {team && <TeamRolLabel teamNaam={label.naam} rol={label.rol} testid="repo-team-rol" />}
    </label>
  );
}
